import React, { useState, useEffect, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { Calendar, CalendarPlus, CalendarDays } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { getCurrentUser } from '../../utils/auth';
import { can } from '../../utils/access';
import { PageHeader, AppButton } from '../ui';
import CommonToaster from '../Common/CommonToaster';
import HolidaysEventsModal from '../Common/HolidaysEventsModal';
import CalendarHeader from './CalendarHeader';
import CalendarGrid from './CalendarGrid';
import WeekGrid from './WeekGrid';
import CalendarEventModal from './CalendarEventModal';
import ConnectCalendarModal from './ConnectCalendarModal';
import { useBranchKey } from '../../contexts/BranchContext';
import './instituteCalendar.scss';

const getWeekStart = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay()); // Sunday-based
  d.setHours(0, 0, 0, 0);
  return d;
};

const InstituteCalendarPage = () => {
  // Branch-specific holidays exist, so the event list changes with the scope.
  const branchKey = useBranchKey();
  const now = new Date();
  const user = getCurrentUser();
  const isAdmin = (user?.usertype === 'superadmin' || user?.usertype === 'admin') && can('calendar.manage');
  
  // Detect mobile
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  /* ── View state ── */
  const [view, setView] = useState(() => window.innerWidth < 768 ? 'day' : 'week'); // 'month' | 'week' | 'day'
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [dayView, setDayView] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  /* ── Data ── */
  const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5, 6]);
  const [savedWorkingDays, setSavedWorkingDays] = useState([1, 2, 3, 4, 5, 6]);
  const [studentSelfScheduling, setStudentSelfScheduling] = useState(false);
  const [savedStudentSelfScheduling, setSavedStudentSelfScheduling] = useState(false);
  const [events, setEvents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [interviewEvents, setInterviewEvents] = useState([]);
  const [showNonWorkingDays, setShowNonWorkingDays] = useState(true);
  const [loading, setLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [eventSaving, setEventSaving] = useState(false);

  /* ── Modal ── */
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [defaultDate, setDefaultDate] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);

  /* ── Holidays & Events modal ── */
  const [hevOpen, setHevOpen] = useState(false);
  const [hevEvents, setHevEvents] = useState([]);
  const [hevLoading, setHevLoading] = useState(false);

  /* ── Toast ── */
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const showToast = (message, type = 'success') => setToast({ show: true, message, type });

  /* ══════════════════════════════════════════════ */
  /*  Data Fetching                                 */
  /* ══════════════════════════════════════════════ */
  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/institute/settings');
      const wd = data.settings?.workingDays ?? [1, 2, 3, 4, 5, 6];
      const sss = !!data.settings?.studentSelfScheduling;
      setWorkingDays(wd);
      setSavedWorkingDays(wd);
      setStudentSelfScheduling(sss);
      setSavedStudentSelfScheduling(sss);
    } catch (err) {
      console.error('Fetch settings error:', err);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      let params;
      if (view === 'week') {
        const from = weekStart.toISOString().split('T')[0];
        const toDate = new Date(weekStart);
        toDate.setDate(toDate.getDate() + 6);
        params = { from, to: toDate.toISOString().split('T')[0] };
      } else if (view === 'day') {
        // Day navigation moves `dayView` without touching `month`, so asking by
        // month here returned the events of whatever month the page opened on —
        // page past a month boundary and the holidays stopped arriving. The
        // endpoint's from/to matches on overlap, so a single day still picks up
        // multi-day holidays that started earlier.
        const dayStr = dayView.toISOString().split('T')[0];
        params = { from: dayStr, to: dayStr };
      } else {
        params = { year, month: month + 1 };
      }
      const { data } = await apiClient.get('/institute/calendar', { params });
      setEvents(data.events || []);
    } catch (err) {
      console.error('Fetch events error:', err);
    }
  }, [view, year, month, weekStart, dayView, branchKey]);

  /* Batches whose classes belong on this calendar.
     `/batches/list` is already scoped by the backend — students get every batch
     they are enrolled in (not just the primary one), instructors get the ones
     assigned to them — so the only job here is asking for enough of them. The
     default page size is 10, which silently truncated a large institute's
     calendar to the ten most recent batches. */
  const fetchBatches = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/batches/list', { params: { pageSize: 200 } });
      const list = data.batches || [];

      if (user?.usertype === 'student') {
        // Every enrollment, past and present: the projection bounds each batch
        // by its own start/end dates, so a finished cohort draws its history and
        // no future classes.
        setBatches(list);
      } else {
        // Staff care about what is running now — a finished batch's slots are
        // not something anyone still has to be anywhere for.
        setBatches(list.filter((b) => b.status !== 'Completed'));
      }
    } catch (err) {
      console.error('Fetch batches error:', err);
    }
  }, [user?.usertype]);

  const fetchInterviewEvents = useCallback(async () => {
    try {
      let params;
      if (view === 'week') {
        params = { startDate: weekStart.toISOString().split('T')[0] };
        const toDate = new Date(weekStart);
        toDate.setDate(toDate.getDate() + 6);
        params.endDate = toDate.toISOString().split('T')[0];
      } else if (view === 'day') {
        const dateStr = dayView.toISOString().split('T')[0];
        params = { startDate: dateStr, endDate: dateStr };
      } else {
        const start = new Date(year, month, 1);
        const end = new Date(year, month + 1, 0);
        params = { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
      }
      const { data } = await apiClient.get('/calendar/me', { params });
      setInterviewEvents(data.events || []);
    } catch (err) {
      console.error('Fetch interview events error:', err);
    }
  }, [view, year, month, weekStart, dayView, branchKey]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchSettings(), fetchEvents(), fetchBatches(), fetchInterviewEvents()]);
      setLoading(false);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Auto-switch to day view on mobile, week view on desktop
      if (mobile && view === 'week') {
        setView('day');
      } else if (!mobile && view === 'day') {
        setView('week');
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [view]);

  // Refetch when navigation or view changes
  useEffect(() => {
    if (!loading) {
      fetchEvents();
      fetchInterviewEvents();
    }
  }, [view, year, month, weekStart, dayView, branchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ══════════════════════════════════════════════ */
  /*  Settings                                      */
  /* ══════════════════════════════════════════════ */
  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      await apiClient.put('/institute/settings', { workingDays, studentSelfScheduling });
      setSavedWorkingDays([...workingDays]);
      setSavedStudentSelfScheduling(studentSelfScheduling);
      showToast('Settings updated.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save settings.', 'error');
    } finally {
      setSettingsSaving(false);
    }
  };

  /* ══════════════════════════════════════════════ */
  /*  Navigation                                    */
  /* ══════════════════════════════════════════════ */
  const goToPrev = () => {
    if (view === 'month') {
      if (month === 0) { setMonth(11); setYear(year - 1); }
      else setMonth(month - 1);
    } else if (view === 'day') {
      const d = new Date(dayView);
      d.setDate(d.getDate() - 1);
      setDayView(d);
    } else {
      const d = new Date(weekStart);
      d.setDate(d.getDate() - 7);
      setWeekStart(d);
    }
  };

  const goToNext = () => {
    if (view === 'month') {
      if (month === 11) { setMonth(0); setYear(year + 1); }
      else setMonth(month + 1);
    } else if (view === 'day') {
      const d = new Date(dayView);
      d.setDate(d.getDate() + 1);
      setDayView(d);
    } else {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + 7);
      setWeekStart(d);
    }
  };

  const goToToday = () => {
    const n = new Date();
    n.setHours(0, 0, 0, 0);
    if (view === 'month') {
      setYear(n.getFullYear());
      setMonth(n.getMonth());
    } else if (view === 'day') {
      setDayView(n);
    } else {
      setWeekStart(getWeekStart(n));
    }
  };

  const handleViewChange = (newView) => {
    if (newView === view) return;
    const n = new Date();
    if (newView === 'week') {
      // Pick week containing today if in current month, else week of 1st
      if (year === n.getFullYear() && month === n.getMonth()) {
        setWeekStart(getWeekStart(n));
      } else {
        setWeekStart(getWeekStart(new Date(year, month, 1)));
      }
    } else if (newView === 'day') {
      // Pick today or first day of current view
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      setDayView(d);
    } else {
      // Switch to month that contains the week/day
      const ref = view === 'day' ? dayView : weekStart;
      setYear(ref.getFullYear());
      setMonth(ref.getMonth());
    }
    setView(newView);
  };

  /* ══════════════════════════════════════════════ */
  /*  Modal handlers                                */
  /* ══════════════════════════════════════════════ */
  const openAddModal = () => { setEditingEvent(null); setDefaultDate(null); setModalOpen(true); };
  const handleDateClick = (date) => { setEditingEvent(null); setDefaultDate(date); setModalOpen(true); };
  const handleEventClick = (event) => { setEditingEvent(event); setDefaultDate(null); setModalOpen(true); };
  const handleCloseModal = () => { setModalOpen(false); setEditingEvent(null); setDefaultDate(null); };

  const handleSaveEvent = async (eventData) => {
    setEventSaving(true);
    try {
      if (eventData._id) {
        await apiClient.put(`/institute/calendar/${eventData._id}`, eventData);
        showToast('Event updated.');
      } else {
        await apiClient.post('/institute/calendar', eventData);
        showToast('Event created.');
      }
      handleCloseModal();
      await fetchEvents();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save event.', 'error');
    } finally {
      setEventSaving(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await apiClient.delete(`/institute/calendar/${eventId}`);
      showToast('Event deleted.');
      handleCloseModal();
      await fetchEvents();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete event.', 'error');
    }
  };

  const openHev = async () => {
    setHevOpen(true);
    if (hevEvents.length === 0) {
      setHevLoading(true);
      try {
        const today = new Date().toISOString().split('T')[0];
        const future = new Date(Date.now() + 365 * 86400000).toISOString().split('T')[0];
        const { data } = await apiClient.get(`/institute/calendar?from=${today}&to=${future}`);
        setHevEvents(data.events || []);
      } catch { /* ignore */ }
      setHevLoading(false);
    }
  };

  /* ══════════════════════════════════════════════ */
  /*  Render                                        */
  /* ══════════════════════════════════════════════ */
  const settingsDirty = JSON.stringify(workingDays) !== JSON.stringify(savedWorkingDays) ||
    studentSelfScheduling !== savedStudentSelfScheduling;

  if (loading) {
    return (
      <div className="ic-loading">
        <Spinner animation="border" size="sm" /> Loading calendar…
      </div>
    );
  }

  return (
    <div className="ic-page">
      {/* Title-only header — portals "Calendar" into the top nav. The
          in-card CalendarHeader below is the month/week toolbar, not a title. */}
      <PageHeader
        title="Calendar"
        icon={Calendar}
        actions={
          <>
            <AppButton variant="outline" size="sm" onClick={openHev}>
              <CalendarDays size={15} /> View Holidays &amp; Events
            </AppButton>
            <AppButton variant="outline" size="sm" onClick={() => setConnectOpen(true)}>
              <CalendarPlus size={15} /> Connect calendar
            </AppButton>
          </>
        }
      />
      {/* Calendar card */}
      <div className="ic-calendar-card">
        <CalendarHeader
          year={year}
          month={month}
          weekStart={weekStart}
          dayView={dayView}
          view={view}
          onViewChange={handleViewChange}
          onPrev={goToPrev}
          onNext={goToNext}
          onToday={goToToday}
          onAddEvent={isAdmin ? openAddModal : null}
          workingDays={workingDays}
          onWorkingDaysChange={isAdmin ? setWorkingDays : null}
          studentSelfScheduling={studentSelfScheduling}
          onStudentSelfSchedulingChange={isAdmin ? setStudentSelfScheduling : null}
          onSaveSettings={isAdmin ? handleSaveSettings : null}
          settingsSaving={settingsSaving}
          settingsDirty={isAdmin ? settingsDirty : false}
          showNonWorkingDays={showNonWorkingDays}
          onToggleNonWorkingDays={() => setShowNonWorkingDays((p) => !p)}
          isAdmin={isAdmin}
        />
        {view === 'month' ? (
          <CalendarGrid
            year={year}
            month={month}
            events={events}
            batches={batches}
            interviewEvents={interviewEvents}
            workingDays={workingDays}
            onDateClick={isAdmin ? handleDateClick : null}
            onEventClick={isAdmin ? handleEventClick : null}
          />
        ) : view === 'day' ? (
          <WeekGrid
            weekStart={dayView}
            events={events}
            batches={batches}
            interviewEvents={interviewEvents}
            workingDays={workingDays}
            onDateClick={isAdmin ? handleDateClick : null}
            onEventClick={isAdmin ? handleEventClick : null}
            isDayView={true}
          />
        ) : (
          <WeekGrid
            weekStart={weekStart}
            events={events}
            batches={batches}
            interviewEvents={interviewEvents}
            workingDays={workingDays}
            showNonWorkingDays={showNonWorkingDays}
            onDateClick={isAdmin ? handleDateClick : null}
            onEventClick={isAdmin ? handleEventClick : null}
          />
        )}
      </div>

      <HolidaysEventsModal
        show={hevOpen}
        onHide={() => setHevOpen(false)}
        events={hevEvents}
        loading={hevLoading}
        onEventClick={isAdmin ? handleEventClick : null}
        isAdmin={isAdmin}
      />

      {isAdmin && (
        <CalendarEventModal
          show={modalOpen}
          onHide={handleCloseModal}
          event={editingEvent}
          defaultDate={defaultDate}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
          saving={eventSaving}
        />
      )}

      <ConnectCalendarModal
        show={connectOpen}
        onHide={() => setConnectOpen(false)}
        onToast={showToast}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((p) => ({ ...p, show: false }))}
      />
    </div>
  );
};

export default InstituteCalendarPage;
